// frontend/src/components/community/ConversationList.js
import React, { useEffect, useState } from 'react';
import { MessageSquare, Search, X, User, Store, Check, CheckCheck, Filter } from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';

const ConversationList = ({ onSelectConversation, filterType = 'all' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFilter, setCurrentFilter] = useState(filterType);
  const { conversations, getConversations, isLoading } = useCommunityStore();
  const { user } = useAuthStore();

  useEffect(() => {
    loadConversations();
  }, [currentFilter]);

  const loadConversations = async () => {
    try {
      const params = {};
      if (currentFilter !== 'all') {
        params.type = currentFilter;
      }
      await getConversations(params);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.floor((today - msgDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      return '';
    }
  };

  const getLastMessageText = (conversation) => {
    if (!conversation.lastMessage) return 'No messages yet';
    
    const isYou = conversation.lastMessageBy === user?.id;
    const prefix = isYou ? 'You: ' : '';
    let content = conversation.lastMessage || '';
    
    if (content.length > 50) {
      content = content.substring(0, 50) + '...';
    }
    return prefix + content;
  };

  const filters = [
    { id: 'all', label: 'All', icon: MessageSquare },
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'business', label: 'Business', icon: Store }
  ];

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = `${conv.user?.firstName} ${conv.user?.lastName}`.toLowerCase();
    const businessName = conv.businessContext?.name?.toLowerCase() || '';
    return name.includes(searchQuery.toLowerCase()) || businessName.includes(searchQuery.toLowerCase());
  });

  if (isLoading && conversations.length === 0) {
    return (
      <div className="space-y-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center space-x-3 px-3 py-2 animate-pulse">
            <div className="h-10 w-10 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-1">
              <div className="h-3 bg-gray-200 rounded w-1/3" />
              <div className="h-2 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mb-3">
          <MessageSquare className="h-7 w-7 text-blue-500" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">No messages yet</h3>
        <p className="text-xs text-gray-500 max-w-sm">
          {currentFilter === 'business' 
            ? 'Business messages appear here'
            : currentFilter === 'personal'
            ? 'Personal messages appear here'
            : 'Start a conversation to see it here'}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Compact Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-3 py-2">
        <h2 className="text-base font-bold text-gray-900 mb-1.5">Messages</h2>
        
        {/* Compact Filter Tabs */}
        <div className="flex space-x-0.5 mb-1.5">
          {filters.map((filter) => {
            const Icon = filter.icon;
            const isActive = currentFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => setCurrentFilter(filter.id)}
                className={`flex items-center px-2.5 py-1 text-xs font-medium transition-all rounded-lg ${
                  isActive 
                    ? 'bg-primary-50 text-primary-600 shadow-neomorph-inset' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-3 w-3 mr-1" />
                {filter.label}
              </button>
            );
          })}
        </div>
        
        {/* Compact Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
            <Search className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages..."
            className="w-full pl-8 pr-7 py-1.5 bg-gray-100 border-0 rounded-lg text-xs placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
            >
              <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 transition-colors" />
            </button>
          )}
        </div>
      </div>

      {/* Conversations List - Compact */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <Search className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">No results found</h3>
            <p className="text-xs text-gray-500">
              No conversations matching "{searchQuery}"
            </p>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const otherUser = conversation.user;
            const hasUnread = conversation.unreadCount > 0;
            const isBusiness = conversation.type === 'business';
            
            return (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                className="w-full flex items-start space-x-2.5 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
              >
                {/* Avatar - Smaller */}
                <div className="relative flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                    {otherUser?.avatar ? (
                      <img 
                        src={otherUser.avatar} 
                        alt={otherUser.firstName}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-semibold text-sm">
                        {otherUser?.firstName?.[0]}{otherUser?.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  
                  {/* Business Badge - Smaller */}
                  {isBusiness && (
                    <div className="absolute -bottom-0.5 -right-0.5 bg-blue-500 rounded-full p-0.5 ring-1.5 ring-white">
                      <Store className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Content - Compact */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center space-x-1 min-w-0 flex-1">
                      <h4 className={`text-sm truncate ${hasUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
                        {otherUser?.firstName} {otherUser?.lastName}
                      </h4>
                      
                      {isBusiness && conversation.businessContext && (
                        <span className="text-[8px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full flex-shrink-0 truncate max-w-[60px]">
                          {conversation.businessContext.name}
                        </span>
                      )}
                    </div>
                    
                    {conversation.lastMessageAt && (
                      <span className="text-[9px] text-gray-400 flex-shrink-0 ml-1">
                        {formatTime(conversation.lastMessageAt)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className={`text-xs truncate flex-1 ${
                      hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
                    }`}>
                      {getLastMessageText(conversation)}
                    </p>
                    
                    {/* Unread Badge - Smaller */}
                    {hasUnread && (
                      <div className="ml-1.5 flex-shrink-0">
                        <div className="min-w-[18px] h-[18px] bg-primary-500 rounded-full flex items-center justify-center px-1">
                          <span className="text-[9px] font-semibold text-white">
                            {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph-inset {
          box-shadow: inset 2px 2px 4px #d0d6e0, inset -2px -2px 4px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ConversationList;